import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Delete, ChefHat, Loader2 } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1538128844159-f08f41bfb169?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const press = (d) => {
    if (pin.length >= 8) return;
    setPin((p) => p + d);
  };
  const back = () => setPin((p) => p.slice(0, -1));

  const submit = async () => {
    if (pin.length < 4) return toast.error("Enter your PIN");
    setBusy(true);
    try {
      const u = await login(pin);
      toast.success(`Welcome, ${u.name}`);
    } catch (e) {
      toast.error(apiErr(e, "Login failed"));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-[#0E0E0E] relative z-10" data-testid="pin-login-screen">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex lg:w-[46%] flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Bistro kitchen" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E0E] via-[#0E0E0E]/85 to-[#0E0E0E]/40" />
        </div>
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#E11D2A] flex items-center justify-center">
            <ChefHat className="h-6 w-6 text-[#0E0E0E]" />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight">Paige's Bistro</span>
        </div>
        <div className="relative z-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E11D2A] mb-3">Closing Checklist</p>
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight max-w-md">
            Every station. Every night. Zero missed steps.
          </h1>
          <p className="text-[#A3A3A3] mt-4 max-w-sm">
            Check off your station tasks, sign out, and the supervisor closes it out — the owner is notified the moment it's done.
          </p>
        </div>
      </div>

      {/* Right pin panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="h-10 w-10 rounded-xl bg-[#E11D2A] flex items-center justify-center">
              <ChefHat className="h-5 w-5 text-[#0E0E0E]" />
            </div>
            <span className="font-heading text-lg font-bold">Paige's Bistro</span>
          </div>

          <h2 className="font-heading text-2xl font-bold text-center">Enter your PIN to start</h2>
          <p className="text-[#6B6B6B] text-sm text-center mt-1 mb-7">Sign in to begin your tasks</p>

          <div className="flex items-center justify-center">
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#6B6B6B]">PIN</label>
          </div>
          <div className="mt-2 flex justify-center gap-2.5" data-testid="pin-input-field">
            {Array.from({ length: Math.max(4, pin.length || 4) }).slice(0, 8).map((_, i) => (
              <div key={`pin-dot-${i}`} className={`h-3.5 w-3.5 rounded-full transition-colors ${i < pin.length ? "bg-[#E11D2A]" : "bg-[#2E2E2E]"}`} />
            ))}
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
              <button
                key={d}
                data-testid={`pin-key-${d}`}
                onClick={() => press(String(d))}
                className="h-16 rounded-2xl bg-[#171717] border border-[#2E2E2E] text-2xl font-heading font-semibold text-[#F5F5F5] active:scale-95 active:bg-[#232323] hover:border-[#E11D2A]/50 transition-all"
              >
                {d}
              </button>
            ))}
            <button onClick={back} data-testid="pin-key-back" className="h-16 rounded-2xl bg-[#171717] border border-[#2E2E2E] flex items-center justify-center text-[#A3A3A3] active:scale-95 hover:border-[#E11D2A]/50 transition-all">
              <Delete className="h-6 w-6" />
            </button>
            <button onClick={() => press("0")} data-testid="pin-key-0" className="h-16 rounded-2xl bg-[#171717] border border-[#2E2E2E] text-2xl font-heading font-semibold active:scale-95 active:bg-[#232323] hover:border-[#E11D2A]/50 transition-all">0</button>
            <button
              data-testid="pin-submit-button"
              onClick={submit}
              disabled={busy}
              className="h-16 rounded-2xl bg-[#E11D2A] text-[#0E0E0E] font-heading font-bold text-lg flex items-center justify-center active:scale-95 hover:bg-[#B91C1C] disabled:opacity-60 transition-all"
            >
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : "Go"}
            </button>
          </div>

          <p className="text-center text-xs text-[#6B6B6B] mt-6">
            Forgot your PIN? Ask the owner to reset it.
          </p>
        </div>
      </div>
    </div>
  );
}
